/**
 * moveToActorWalk — the "Move To Actor" node walked via kimodo "Walk here"
 * instead of movementComponent's stride-less glide. Still fires
 * `onMoveCompleted` ({ result: success|failed|aborted, reason? }), read from
 * walkHereState. Dependencies injected. See tests/unit/moveToActorWalk.test.mjs.
 */

/**
 * Walk to `target`, report the outcome once. False if it does not resolve.
 *
 * @param {object} args
 * @param {string} args.target    anchor / marker / scene-object name
 * @param {(t:string)=>number[]|null} args.resolve  name → world point
 * @param {(ctx:{point:number[]})=>unknown} args.walk  starts the walk (runWalkHere)
 * @param {{getState:Function, subscribe:Function}} args.state  walkHereState
 * @param {(result:object)=>void} args.onCompleted  fires onMoveCompleted
 */
export function startMoveToActorWalk({ target, resolve, walk, state, onCompleted }) {
  const point = resolve(target)
  if (!point) {
    onCompleted({ result: 'failed', reason: 'moveToActor: target not resolvable' })
    return false
  }

  // runWalkHere takes its requestId synchronously; a later id means another walk
  // replaced this one.
  const id = state.getState().requestId + 1
  let done = false
  let unsubscribe = () => {}
  const finish = (result) => {
    if (done) return
    done = true
    unsubscribe()
    onCompleted(result)
  }
  const check = () => {
    const { phase, requestId, message } = state.getState()
    if (requestId < id) return
    if (requestId !== id) return finish({ result: 'aborted', reason: 'superseded by another walk' })
    // "already here (0.12 m)" counts as arrival, as it did for the glide.
    if (phase === 'error' && String(message ?? '').startsWith('already here')) return finish({ result: 'success' })
    if (phase === 'error') return finish({ result: 'failed', reason: message ?? 'walk failed' })
    if (phase === 'arrived') {
      return finish(message === 'stopped short'
        ? { result: 'failed', reason: 'stopped short' }
        : { result: 'success' })
    }
  }
  unsubscribe = state.subscribe(check)
  walk({ point })
  check()
  return true
}
